// Dotted thought-orb renderer for the strategy dock. Two modes:
//   'composing' — a strategy's avatar: a fat dotted TORUS whose tube radius is modulated by
//     travelling Rayleigh–Plateau necks, so it reads as a liquid loop rather than a ball.
//   'shaping'  — the empty-state invite: a shape of tiny square pixels morphing through a
//     polygon cycle.
// Plain 2D canvas fills only (no ctx.filter) so it paints identically across browsers. Colors are
// single-hue HSL luminance ramps (dark far → bright near) so depth never muddies into grey. Pure
// functions + one resilient, monotonic shared rAF clock; StrategyOrb.tsx owns lifecycle.

// A single-hue palette: fixed hue+saturation, lightness ramps with depth. Keeps
// the orb vivid — no muddy grey midpoint from cross-hue RGB interpolation.
export interface OrbPalette {
	h: number; // hue 0..360
	s: number; // saturation 0..100
	lFar: number; // lightness of the deepest (back) dots
	lNear: number; // lightness of the nearest (front) dots
	// How many points of lightness hover may add to the near edge. Per-hue, because how bright a
	// colour can go before a graphical mark stops clearing 3:1 on the dock varies enormously —
	// a violet has three times the headroom of a yellow.
	lGlow?: number;
}

export interface OrbMotion {
	mode: 'composing' | 'shaping';
	// Radians/sec the necks travel around the loop. Magnitude only — see `spinDir`. Status sets
	// this; identity never does, so a strategy going active doesn't become someone else.
	spinRate: number;
	// +1 or -1. Which way the necks travel. Kept separate from `spinRate` because the two answer
	// to different owners: direction is part of who the agent IS, speed is what it is doing.
	spinDir: number;
	// How many necks travel the loop (the silhouette signature). Composing only.
	necks: number;
	// How deeply the tube pinches. Clamped by the renderer: past the ceiling the neck dots shrink
	// under the raster and the pinch stops being drawable.
	neckDepth: number;
	// Scales the morph cadence (shaping only).
	undulate: number;
	// 0 = steady; >0 = the whole orb brightens/swells on a slow pulse (attention).
	breathe: number;
	// 0 at rest, 1 while the pointer is over the orb. Quickens the sash and deepens its
	// undulation — the agent noticing you, not a separate animation.
	hover?: number;
	// Seconds since the orb was last pressed, or null when it has never been. A single ripple
	// travels out from the moment of contact and fades; the renderer needs the AGE rather than a
	// decaying strength, because the crest has to keep moving forward while it dies away.
	pressAge?: number | null;
}

// HSL → [r,g,b] in 0..255. h in [0,360), s/l in [0,100].
function hslRgb(h: number, s: number, l: number): [number, number, number] {
	const sn = Math.min(100, Math.max(0, s)) / 100;
	const ln = Math.min(100, Math.max(0, l)) / 100;
	const c = (1 - Math.abs(2 * ln - 1)) * sn;
	const hp = (((h % 360) + 360) % 360) / 60;
	const x = c * (1 - Math.abs((hp % 2) - 1));
	let r = 0;
	let g = 0;
	let b = 0;
	if (hp < 1) [r, g, b] = [c, x, 0];
	else if (hp < 2) [r, g, b] = [x, c, 0];
	else if (hp < 3) [r, g, b] = [0, c, x];
	else if (hp < 4) [r, g, b] = [0, x, c];
	else if (hp < 5) [r, g, b] = [x, 0, c];
	else [r, g, b] = [c, 0, x];
	const m = ln - c / 2;
	return [
		Math.round((r + m) * 255),
		Math.round((g + m) * 255),
		Math.round((b + m) * 255),
	];
}

// HSL → "r,g,b" string. h in [0,360), s/l in [0,100].
function hsl(h: number, s: number, l: number): string {
	return hslRgb(h, s, l).join(',');
}

// Smallest radius a dot is ever drawn at. The sash is deliberately made of many fine marks, so
// this sits low: clamping harder would fuse the strands into a solid band.
const DOT_MIN_R = 0.3;

interface Dot {
	x: number;
	y: number;
	z: number;
	r: number;
	fill: string;
}

// Draw one frame into `ctx` at logical `size` px (dpr already applied). t = sec.
export function drawOrb(
	ctx: CanvasRenderingContext2D,
	size: number,
	t: number,
	palette: OrbPalette,
	motion: OrbMotion,
	// Accumulated wave phase. Defaults to deriving one from `t`, which is correct whenever the
	// rate has been constant — the React shell passes its own so a rate change never jumps.
	phase?: number,
): void {
	ctx.clearRect(0, 0, size, size);
	if (size <= 0) return;

	const cx = size / 2;
	const cy = size / 2;

	const pulse =
		motion.breathe > 0 ? 0.5 + 0.5 * Math.sin(t * motion.breathe) : 0;

	const dots: Dot[] = [];
	if (motion.mode === 'shaping') {
		drawShaping(ctx, t, size, cx, cy, (size / 2) * 0.8, palette, motion, pulse);
	} else {
		drawRibbon(
			dots,
			phase ?? t * densityFor(size).speed * motion.spinRate,
			size,
			cx,
			cy,
			palette,
			motion,
			pulse,
		);
	}

	// Painter's algorithm: far dots first so near dots overlap them.
	dots.sort((a, b) => a.z - b.z);
	for (const d of dots) {
		ctx.fillStyle = d.fill;
		ctx.beginPath();
		ctx.arc(d.x, d.y, Math.max(DOT_MIN_R, d.r), 0, Math.PI * 2);
		ctx.fill();
	}
}

// ── The composing ribbon ───────────────────────────────────────────────────
// An undulating sash of parallel strands riding a great circle over a faint ghost sphere. This is
// a port of the "composing" state from Jakub Antalik's thinking-orbs (MIT), structure and tuned
// constants kept faithful; what differs is that each strategy paints in its own hue instead of
// grayscale, since here the orb is an identity and not just a spinner.
//
// The band's orientation is FROZEN — the reference's tuned preset sets spin to 0. All the life
// comes from two traveling waves running along the sash, not from tumbling the whole thing. That
// is the difference between reading as a thought forming and reading as a spinning ball.

// How long a press ripple takes to travel the band and fade. 360ms is the dock's own sanctioned
// beat, reused rather than invented.
const PRESS_MS = 360;

// Sphere radius as a fraction of the box. Nearly the whole box: the dock draws the empty slot as a
// dashed ring at the full `--orb-size`, and a filled avatar has to read at that same diameter or
// the dock looks like it holds two different-sized things. The reference's own 0.78 leaves a
// margin that is fine on its dark demo page and too much here.
export const SPHERE_R = 0.92;
// The camera never yaws (spin 0); it only tilts, so the band is seen slightly from above.
const VIEW_TILT = 0.3;

// Dot radii were tuned against a 300pt frame; sub-linear scaling keeps a 20px mark legible.
function radiusScale(size: number): number {
	return Math.pow(size / 300, 0.6);
}

// Stable directions on a unit sphere (Fibonacci lattice) — the faint ghost the sash rides over.
function fibDir(i: number, n: number): [number, number, number] {
	const golden = Math.PI * (3 - Math.sqrt(5));
	const y = 1 - (2 * (i + 0.5)) / n;
	const rad = Math.sqrt(1 - y * y);
	const a = i * golden;
	return [rad * Math.cos(a), y, rad * Math.sin(a)];
}

// Density and radii, resolved per size the way the reference's presets do: counts scale by the
// square root so total dots scale linearly, and radii scale directly. These are its tuned 20px
// and 64px rows — the dock paints at 20 and a card at 24, so the small row governs both.
interface RibbonDensity {
	lanes: number;
	segs: number;
	ghostN: number;
	rBase: number;
	rDepth: number;
	speed: number;
}

// Radii are scaled up from the reference's own rows. Its values were tuned for a dark page, where
// a sub-pixel dot still glows; on this light dock the far half of the sash lands at a quarter of a
// near dot's AREA, and that — stacked with its lower alpha and lighter ink — is what left the
// bottom of the band looking unfinished. Scaling every radius together keeps the depth read intact
// while putting the smallest marks back on the raster.
const SMALL: RibbonDensity = {
	lanes: 10,
	segs: 20,
	ghostN: 8,
	rBase: 1.77,
	rDepth: 2.74,
	speed: 3.12,
};

const LARGE: RibbonDensity = {
	lanes: 12,
	segs: 44,
	ghostN: 38,
	rBase: 1.31,
	rDepth: 2.02,
	speed: 2.34,
};

function densityFor(size: number): RibbonDensity {
	return size >= 40 ? LARGE : SMALL;
}

// The sash's off-plane displacement all the way around the band, at one instant. Exported because
// this is where the animation lives, and jsdom has no canvas — it is the only layer a unit test
// can see.
export function ribbonWave(t: number, motion: OrbMotion, size = 20): number[] {
	const d = densityFor(size);
	const dir = motion.spinDir < 0 ? -1 : 1;
	// Tests drive this with plain seconds; the renderer feeds it an accumulated phase. Both are
	// the same quantity when the rate has been constant, which is what a test wants.
	const time = t * d.speed * motion.spinRate;
	const hover = motion.hover ?? 0;
	const swell = 1 + 0.3 * hover;
	const age = motion.pressAge;
	const out: number[] = [];
	for (let w = 0; w < d.lanes; w++) {
		for (let k = 0; k < d.segs; k++) {
			const a = (k / d.segs) * 2 * Math.PI;
			const ripple =
				age != null && age < PRESS_MS / 1000
					? 0.16 *
						(1 - age / (PRESS_MS / 1000)) ** 2 *
						Math.sin(a * 2 - age * 11 * dir)
					: 0;
			out.push(
				(0.16 * Math.sin(a * 3 - time * 1.7 * dir + w * 0.22) +
					0.07 * Math.sin(a * 5 + time * 1.1 * dir)) *
					swell +
					ripple,
			);
		}
	}
	return out;
}

// How much faster the sash runs while the pointer is over it.
export const HOVER_RATE = 1.7;

export function ribbonDensity(size: number): RibbonDensity {
	return densityFor(size);
}

// How far apart the sash's parallel strands sit, which hover widens. Exported so the deformation
// ceiling can be checked against the spacing that actually ships.
export function ribbonLanePitch(hover = 0): number {
	return 0.075 * (1 + 0.34 * hover);
}

// 'composing': the strategy avatar — an undulating dotted sash riding a great circle.
//
// `phase` is ACCUMULATED by the caller rather than derived from `t`. The wave's argument is
// phase * 1.7, so recomputing it from absolute time the moment hover changes the rate would jump
// the sash three whole cycles — a teleport, not a speed-up.
function drawRibbon(
	dots: Dot[],
	phase: number,
	size: number,
	cx: number,
	cy: number,
	palette: OrbPalette,
	motion: OrbMotion,
	pulse: number,
): void {
	const R = (size / 2) * SPHERE_R;
	const rs = radiusScale(size);
	const d = densityFor(size);
	// Direction is per-strategy; the reference has a single sense of travel.
	const dir = motion.spinDir < 0 ? -1 : 1;
	const time = phase;
	const hover = motion.hover ?? 0;
	const pressAge = motion.pressAge;

	// The camera: no yaw, a fixed tilt. Kept as a closure so the ghost and the sash share it.
	const st = Math.sin(VIEW_TILT);
	const ct = Math.cos(VIEW_TILT);
	const project = (
		x: number,
		y: number,
		z: number,
	): [number, number, number] => {
		const y1 = y * ct - z * st;
		const z2 = y * st + z * ct;
		return [cx + x, cy - y1, z2];
	};

	// The ghost sphere the sash rides over — barely there, but it is what makes the sash read as
	// wrapping something rather than floating.
	const ghostL = (palette.lFar + palette.lNear) / 2;
	for (let i = 0; i < d.ghostN; i++) {
		const dirV = fibDir(i, d.ghostN);
		const [px, py, z] = project(dirV[0] * R, dirV[1] * R, dirV[2] * R);
		const depth = (z / R + 1) / 2;
		dots.push({
			x: px,
			y: py,
			z,
			r: 0.8 * rs,
			fill: `rgba(${hsl(palette.h, palette.s * 0.5, ghostL)},${(0.1 + 0.22 * depth).toFixed(3)})`,
		});
	}

	// The band plane. Frozen: with no precession the sash holds its orientation and every bit of
	// motion you see is the undulation traveling along it.
	const ta = 0.55;
	const ux = 1;
	const uz = 0;
	const vx = 0;
	const vy = Math.cos(ta);
	const vz = Math.sin(ta);
	// Plane normal, u x v.
	const nx = -uz * vy;
	const ny = uz * vx - ux * vz;
	const nz = ux * vy;

	const half = (d.lanes - 1) / 2;
	// Hover reads through the channels that survive a 20px raster. A deeper wave alone is worth
	// about half a pixel here and effectively invisible; the band SPREADING and the dots FATTENING
	// are each worth a whole one, and they compound with the quicker travel.
	const lanePitch = 0.075 * (1 + 0.34 * hover);
	const dotSwell = 1 + 0.22 * hover;
	for (let w = 0; w < d.lanes; w++) {
		const laneOff = (w - half) * lanePitch;
		const edge = Math.abs(w - half) / Math.max(1, half);
		for (let k = 0; k < d.segs; k++) {
			const a = (k / d.segs) * 2 * Math.PI;
			// Two traveling waves along the band, at different rates and wavelengths, so the sash
			// never settles into a pattern you can read as a loop. Hover deepens both — the same
			// motion, gathered, rather than a second animation layered on top.
			const swell = 1 + 0.3 * hover;
			// The press ripple: one crest sweeping around the band from where it was struck,
			// riding over the undulation rather than replacing it. Its phase advances with the
			// ripple's own age while its amplitude decays, so the crest keeps travelling forward
			// as it fades. Tying the phase to a decaying strength instead would run it backwards.
			const ripple =
				pressAge != null && pressAge < PRESS_MS / 1000
					? 0.16 *
						// Hover has already spread the band; a full-strength ripple on top of that
						// pushes the sash past where it holds together as one shape.
						(1 - 0.4 * hover) *
						(1 - pressAge / (PRESS_MS / 1000)) ** 2 *
						Math.sin(a * 2 - pressAge * 11 * dir)
					: 0;
			const wob =
				(0.16 * Math.sin(a * 3 - time * 1.7 * dir + w * 0.22) +
					0.07 * Math.sin(a * 5 + time * 1.1 * dir)) *
					swell +
				ripple;
			const off = laneOff + wob;
			// A point on the ring, pushed off-plane by the wave, then normalised back onto the
			// sphere so the sash stays wrapped to its surface.
			const x = ux * Math.cos(a) + vx * Math.sin(a) + nx * off;
			const y = vy * Math.sin(a) + ny * off;
			const z = uz * Math.cos(a) + vz * Math.sin(a) + nz * off;
			const len = Math.hypot(x, y, z) || 1;
			const [px, py, zr] = project((x / len) * R, (y / len) * R, (z / len) * R);
			const depth = (zr / R + 1) / 2;
			// Depth is carried by the mark itself, never by blur: the NEAR edge of the sash is
			// darkest and fully opaque, the far edge fades pale and translucent. The outer lanes
			// lighten too, so the band dissolves at its edges rather than ending in a hard line.
			// Outer lanes lighten so the band dissolves at its edges instead of stopping at a hard
			// line — but only on the near side. The far side is already pale and half-transparent,
			// and lightening it further is what left the bottom of the sash looking unfinished.
			// Hover lights the sash up: the ink goes brighter and more vivid, so the loop reads as
			// glowing rather than merely darker. The lift is capped by hue — each entry's ceiling
			// is the lightest it can go while a graphical mark still clears 3:1 on the dock — and
			// applied on top of the depth ramp so the near edge glows most.
			const glow = hover * (palette.lGlow ?? 0) * depth;
			const l =
				palette.lFar +
				(palette.lNear - palette.lFar) * depth +
				8 * edge * depth -
				3 * pulse +
				glow;
			const saturation = Math.min(100, palette.s + 24 * hover);
			dots.push({
				x: px,
				y: py,
				z: zr,
				r:
					(d.rBase + d.rDepth * depth) *
					(1 - 0.25 * edge) *
					rs *
					dotSwell *
					(1 + 0.1 * pulse),
				// The far side of the sash is both paler AND more transparent, and the two stack: at
				// the reference's 0.4 floor the receding edge composites to 1.3:1 on the dock and
				// all but vanishes, leaving the mark looking like a half-drawn arc. Lifting the
				// floor keeps the band closed all the way round while depth still reads.
				fill: `rgba(${hsl(palette.h, saturation, l)},${(0.82 + 0.18 * depth).toFixed(3)})`,
			});
		}
	}
}

// Closed 2D shapes for 'shaping', each parameterised by fraction f∈[0,1) starting
// top-centre, clockwise, in unit space (roughly ±0.85 of R). Circle + polygons.
type ShapePath = (f: number) => [number, number];

const SHAPE_CIRCLE: ShapePath = (f) => {
	const a = -Math.PI / 2 + f * 2 * Math.PI;
	return [Math.cos(a) * 0.82, Math.sin(a) * 0.82];
};

function polyPath(verts: ReadonlyArray<readonly [number, number]>): ShapePath {
	const n = verts.length;
	const seg: number[] = [];
	let total = 0;
	for (let i = 0; i < n; i++) {
		const a = verts[i];
		const b = verts[(i + 1) % n];
		const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
		seg.push(l);
		total += l;
	}
	return (f) => {
		let target = f * total;
		let i = 0;
		while (target > seg[i] && i < n - 1) {
			target -= seg[i];
			i++;
		}
		const a = verts[i];
		const b = verts[(i + 1) % n];
		const ff = seg[i] ? Math.min(1, target / seg[i]) : 0;
		return [a[0] + (b[0] - a[0]) * ff, a[1] + (b[1] - a[1]) * ff];
	};
}

// Regular n-gon with a vertex at top-centre, walked clockwise (same convention
// as SHAPE_CIRCLE) so the morph doesn't twist.
function regularPolyVerts(
	sides: number,
	radius: number,
): Array<[number, number]> {
	const v: Array<[number, number]> = [];
	for (let i = 0; i < sides; i++) {
		const a = -Math.PI / 2 + (i / sides) * 2 * Math.PI;
		v.push([Math.cos(a) * radius, Math.sin(a) * radius]);
	}
	return v;
}

// Triangle + square, both walked from top-centre clockwise so the morph doesn't
// twist. The square uses a 5-vertex walk to start mid-top-edge.
const SHAPE_TRIANGLE = polyPath([
	[0, -0.9],
	[0.82, 0.55],
	[-0.82, 0.55],
]);
const SHAPE_SQUARE = polyPath([
	[0, -0.72],
	[0.72, -0.72],
	[0.72, 0.72],
	[-0.72, 0.72],
	[-0.72, -0.72],
]);
const SHAPE_DIAMOND = polyPath(regularPolyVerts(4, 0.9));
const SHAPE_PENTAGON = polyPath(regularPolyVerts(5, 0.88));
const SHAPE_HEXAGON = polyPath(regularPolyVerts(6, 0.86));
const SHAPE_CYCLE: ShapePath[] = [
	SHAPE_CIRCLE,
	SHAPE_TRIANGLE,
	SHAPE_SQUARE,
	SHAPE_DIAMOND,
	SHAPE_PENTAGON,
	SHAPE_HEXAGON,
];

// One hue per shape, sampled from the empty-chat aurora blend + the primary
// accent (the same design.md palette family the invite orb draws from):
//   circle   → aurora sky-blue  #6CCEF5 (h197)
//   triangle → aurora peach     #F5C48A (h33)
//   square   → aurora lavender  #C8BEF0 (h252)
//   diamond  → aurora gold      #F5D8A0 (h40)
//   pentagon → aurora purple    #C4A4D4 (h280)
//   hexagon  → primary accent   #3b63fb (h226)
// Saturation is pinned per-hue; lightness comes from the palette's dark ramp so
// every colour reads on the ivory/glass. Colour lerps with the shape morph.
const SHAPE_HUES: ReadonlyArray<readonly [number, number]> = [
	[197, 78],
	[33, 76],
	[252, 55],
	[40, 80],
	[280, 52],
	[226, 80],
];

function smoothstep(x: number): number {
	return x * x * (3 - 2 * x);
}

// Signed distance from unit point (x,y) to the closed polygon `poly`: POSITIVE
// inside, negative outside, magnitude = distance to the nearest edge. Used to
// anti-alias each square pixel by coverage so cells fade in/out smoothly at the
// outline instead of popping on/off frame-to-frame (the "jump").
function signedDistToPoly(
	x: number,
	y: number,
	poly: ReadonlyArray<readonly [number, number]>,
): number {
	let inside = false;
	let d2 = Infinity;
	for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
		const xi = poly[i][0];
		const yi = poly[i][1];
		const xj = poly[j][0];
		const yj = poly[j][1];
		if (
			yi > y !== yj > y &&
			x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-9) + xi
		)
			inside = !inside;
		// Distance² from (x,y) to segment i→j.
		const ex = xj - xi;
		const ey = yj - yi;
		const wx = x - xi;
		const wy = y - yi;
		const tt = Math.max(
			0,
			Math.min(1, (wx * ex + wy * ey) / (ex * ex + ey * ey || 1e-9)),
		);
		const dx = wx - ex * tt;
		const dy = wy - ey * tt;
		const dd = dx * dx + dy * dy;
		if (dd < d2) d2 = dd;
	}
	const d = Math.sqrt(d2);
	return inside ? d : -d;
}

// 'shaping': a shape cycling circle → triangle → square → diamond → … — the
// empty / no-strategy invite. Each frame blends the two neighbouring shape paths
// into one closed outline, then FILLS its interior with a grid of tiny square
// pixels (a dot-matrix look, squares instead of round dots). The pixel colour
// also lerps shape→shape (SHAPE_HUES), so every shape has its own hue.
function drawShaping(
	ctx: CanvasRenderingContext2D,
	t: number,
	size: number,
	cx: number,
	cy: number,
	R: number,
	palette: OrbPalette,
	motion: OrbMotion,
	pulse: number,
): void {
	const HOLD = 1.3;
	const MORPH = 0.9;
	const SEG = HOLD + MORPH;
	const K = SHAPE_CYCLE.length;
	const speed = motion.undulate || 1;
	const tc = (t * speed) % (SEG * K);
	// Modulo-guard k: floating point can land tc at exactly SEG*K → floor gives K,
	// indexing past the cycle (undefined path). Wrap to keep it in [0, K).
	// Guard against a non-finite/negative t (a bad clock reading during mount)
	// producing an out-of-range index — SHAPE_CYCLE[k] must always be a function.
	const kRaw = Number.isFinite(tc) ? Math.floor(tc / SEG) : 0;
	const k = ((kRaw % K) + K) % K;
	const local = Number.isFinite(tc) ? tc - kRaw * SEG : 0;
	const m = local > HOLD ? smoothstep((local - HOLD) / MORPH) : 0;
	const pA = SHAPE_CYCLE[k];
	const pB = SHAPE_CYCLE[(k + 1) % K];

	// Sample the blended outline finely into a unit-space polygon. The shape is
	// held at a FIXED scale (no per-frame breathing) — breathing the geometry
	// against the static pixel grid made edge cells shimmer/boil even at rest. The
	// morph is the only motion, so transitions read as clean and seamless.
	const M = 180;
	const poly: Array<[number, number]> = [];
	for (let i = 0; i < M; i++) {
		const f = i / M;
		const a = pA(f);
		const b = pB(f);
		poly.push([a[0] + (b[0] - a[0]) * m, a[1] + (b[1] - a[1]) * m]);
	}

	// A square-pixel grid across the unit shape. Each cell's alpha is its COVERAGE
	// by the blended outline (signed distance, feathered over ~one cell width), so
	// boundary pixels fade in/out smoothly as the shape morphs — no popping/jump.
	// Denser on bigger orbs so it always reads as many tiny squares.
	const N = Math.max(18, Math.min(48, Math.round(size * 1.1)));
	const step = 2 / N; // unit-space cell pitch (shape spans roughly -1..1)
	const px = step * R * 0.7; // device-px square edge, small gap between cells
	const feather = step * 0.9; // signed-distance band over which a cell fades
	// One flat colour per shape: each shape holds a single hue for its whole HOLD,
	// then crossfades DIRECTLY (RGB) to the next shape's colour during the morph —
	// so a colour belongs to a shape, not to an intermediate blend of hues.
	const [hA, sA] = SHAPE_HUES[k];
	const [hB, sB] = SHAPE_HUES[(k + 1) % K];
	const ca = hslRgb(hA, sA, palette.lNear);
	const cb = hslRgb(hB, sB, palette.lNear);
	const rgb = `${Math.round(ca[0] + (cb[0] - ca[0]) * m)},${Math.round(
		ca[1] + (cb[1] - ca[1]) * m,
	)},${Math.round(ca[2] + (cb[2] - ca[2]) * m)}`;
	// A gentle whole-shape brightness breathe (uniform, not geometric) so the orb
	// feels alive during the holds without any edge shimmer.
	const glow = 0.9 + 0.1 * pulse;
	for (let gy = 0; gy < N; gy++) {
		const uy = -1 + (gy + 0.5) * step;
		for (let gx = 0; gx < N; gx++) {
			const ux = -1 + (gx + 0.5) * step;
			const sd = signedDistToPoly(ux, uy, poly);
			// Coverage 0→1 across the feather band centred on the outline.
			const cov = Math.min(1, Math.max(0, 0.5 + sd / feather));
			if (cov <= 0.01) continue;
			ctx.fillStyle = `rgba(${rgb},${(glow * cov).toFixed(3)})`;
			ctx.fillRect(cx + ux * R - px / 2, cy + uy * R - px / 2, px, px);
		}
	}
}

// ── Shared clock ───────────────────────────────────────────────────────────
// All live orbs paint on ONE requestAnimationFrame so they stay in phase and
// cost one loop regardless of count. It is:
//  - monotonic: startMs is set ONCE, so t never jumps when orbs (un)subscribe;
//  - resilient: a throw in one orb's frame can't kill the loop (each is guarded
//    and the next frame is always scheduled) — the root cause of the whole
//    field freezing at a "rest" once any single frame errored;
//  - idle-aware: stops entirely when no orb is mounted, skips paint when hidden.
type FrameFn = (t: number) => void;
const frames = new Set<FrameFn>();
let rafId = 0;
let startMs = 0;
let loggedFrameError = false;

function loop(now: number): void {
	const t = (now - startMs) / 1000;
	const hidden =
		typeof document !== 'undefined' && document.visibilityState === 'hidden';
	if (!hidden) {
		for (const f of frames) {
			try {
				f(t);
			} catch (err) {
				// One bad frame must never stop the shared loop. Surface it once so a
				// real renderer bug isn't invisible (this hid an off-canvas scaling bug).
				if (!loggedFrameError) {
					loggedFrameError = true;

					console.error('[strategy orb] frame error', err);
				}
			}
		}
	}
	rafId = requestAnimationFrame(loop);
}

// "Now" on the shared clock's own timeline. A press has to be stamped against the same origin the
// frame callback sees, or the ripple's age is meaningless — the clock starts when the first orb
// mounts, not at page load.
export function orbNow(): number {
	if (startMs === 0) startMs = performance.now();
	return (performance.now() - startMs) / 1000;
}

export function addOrbFrame(fn: FrameFn): () => void {
	frames.add(fn);
	if (!rafId) {
		if (startMs === 0) startMs = performance.now();
		rafId = requestAnimationFrame(loop);
	}
	return () => {
		frames.delete(fn);
		if (frames.size === 0 && rafId) {
			cancelAnimationFrame(rafId);
			rafId = 0;
		}
	};
}
